import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, getSessionId } from "@/lib/api";
import { ArrowRight, FileText, Plus } from "lucide-react";

export default function MyPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get(`/plans?session_id=${getSessionId()}`)
      .then((r) => { if (!cancelled) setPlans(r.data.plans || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight">Your plans</h1>
            <p className="text-zinc-500 mt-2 text-sm">Every plan you&apos;ve generated in this browser.</p>
          </div>
          <Link to="/generate" data-testid="new-plan-btn" className="btn-lift inline-flex items-center gap-2 rounded-full bg-indigo-600 text-white h-11 px-5 hover:bg-indigo-700">
            <Plus className="h-4 w-4" /> New plan
          </Link>
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="text-zinc-400 text-sm">Loading…</div>
          ) : plans.length === 0 ? (
            <div className="text-center py-24 bg-white border border-dashed border-zinc-300 rounded-3xl">
              <FileText className="h-10 w-10 text-zinc-300 mx-auto" />
              <div className="mt-4 font-display font-black text-2xl">No plans yet</div>
              <p className="text-zinc-500 text-sm mt-2">Paste your website and get your next moves in ~90 seconds.</p>
              <Link to="/generate" data-testid="empty-cta" className="btn-lift inline-flex items-center gap-2 mt-6 rounded-full bg-zinc-900 hover:bg-indigo-600 text-white h-11 px-5">
                Get my next moves <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {plans.map((p) => (
                <Link
                  key={p.id}
                  to={`/plan/${p.id}`}
                  data-testid={`plan-card-${p.id}`}
                  className="card-hover bg-white border border-zinc-200 rounded-2xl p-6 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs uppercase tracking-widest text-zinc-400">{new Date(p.created_at).toLocaleDateString()}</div>
                      {p.paid && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Paid</span>}
                    </div>
                    <div className="font-display font-black text-2xl mt-2">{p.input?.business_name}</div>
                    <div className="text-sm text-zinc-500 mt-1">{p.input?.business_type} · {p.input?.location}</div>
                    <p className="text-sm text-zinc-600 mt-3 line-clamp-3">{p.plan?.executive_summary}</p>
                  </div>
                  <div className="mt-4 text-sm text-indigo-600 flex items-center gap-1">Open plan <ArrowRight className="h-4 w-4" /></div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
